import { mockAcademicsCourses } from '../../__mocks__/academics/academicsCourses.mock';
import { mockAcademicsClasses } from '../../__mocks__/academics/academicsClasses.mock';
import isAppWithMock from 'src/utils/isAppWithMock';
import {
  IAcademicsClassResponse,
  IAcademicsCourseResponse,
  IAcademicsMaterialsResponse,
  IClassListResponse,
  ICourseListResponse,
  IScheduleListResponse,
  IAssessmentListResponse,
  ILessonPlanListResponse,
  IClassSingleResponse,
  ICourseSingleResponse,
  IScheduleSingleResponse,
  IAssessmentSingleResponse,
  ILessonPlanSingleResponse,
} from './academics.interfaces';
import {
  IAcademicsSchedule,
  IAcademicsScheduleResponse,
  IScheduleRequest,
  IScheduleListQueryParams,
} from './academicsSchedules.interfaces';
import { mockAcademicsSchedules } from '../../__mocks__/academics/academicsSchedules.mock';
import { mockAcademicsMaterials } from '../../__mocks__/academics/academicsMaterials.mock';
import {
  IAcademicsAssessmentClassMapResponse,
  IAcademicsAssessmentCourseMapResponse,
  IAcademicsAssessmentDetails,
  IAssessmentRequest,
  IAssessmentListQueryParams,
  IUpdateSubjectScoresRequest,
} from './academicsAssessments.interfaces';
import {
  academicsAssessmentCoursesMock,
  academicsAssessmentCourseStatisticsMock,
} from '../../__mocks__/academics/academicsAssessmentCourses.mock';
import {
  academicsAssessmentClassesMock,
  mockAcademicsAssessmentClassStatistics,
} from '../../__mocks__/academics/academicsAssessmentClasses.mock';
import {
  IAcademicsLibraryItem,
  IAcademicsLibraryResponse,
  IBorrowingRecord,
  ILibraryBorrowingRequest,
  ILibraryBorrowingResponse,
  BorrowerType,
} from './academicsLibrary.interfaces';
import { mockAcademicsLibrary } from '../../__mocks__/academics/academicsLibrary.mock';
import { createHttpService } from '../ApiService';
import {
  TAcademicMaterial,
  IBook,
  ILabEquipment,
  IDigitalResource,
  IStationery,
  IAcademicsMaterials,
} from './academicsMaterials.interfaces';
import {
  ILessonPlanResponse,
  ILessonPlanRequest,
  ILessonPlanListQueryParams,
} from './academicsLessonPlans.interfaces';
import { mockAcademicsLessonPlanList } from 'src/__mocks__/academics/academicsLessonPlanList.mock';
import { TPaginationParams } from '../__shared/shared.interfaces';
import { academicAssessmentDetailsMock } from 'src/__mocks__/academics/academicAssessmentDetails.mock';
import {
  IClassRequest,
  IClassListQueryParams,
  ICourseRequest,
  ICourseListQueryParams,
} from './academicsClassesAndCourses.interfaces';

export const useAcademicsService = () => {
  const httpAPI = createHttpService();
  const isMockData = isAppWithMock();
  const endpoint = '/academics';

  // ============================================================
  // Classes API
  // ============================================================

  const getClasses = async (
    params?: IClassListQueryParams
  ): Promise<IClassListResponse> => {
    if (isMockData) {
      return Promise.resolve({
        classes: mockAcademicsClasses as any,
        total: mockAcademicsClasses.length,
        pagination: {
          page: params?.page || 1,
          limit: params?.limit || 10,
          total: mockAcademicsClasses.length,
          totalPages: Math.ceil(mockAcademicsClasses.length / (params?.limit || 10)),
        },
      });
    }
    return httpAPI.get<IClassListResponse>(`${endpoint}/classes`, { params });
  };

  const getClassById = async (classId: string): Promise<IClassSingleResponse> => {
    if (isMockData) {
      const mockClass = mockAcademicsClasses[0];
      return Promise.resolve({ class: mockClass as any });
    }
    return httpAPI.get<IClassSingleResponse>(`${endpoint}/classes/${classId}`);
  };

  const createClass = async (data: IClassRequest): Promise<IClassSingleResponse> => {
    if (isMockData) {
      return Promise.resolve({ class: mockAcademicsClasses[0] as any });
    }
    return httpAPI.post<IClassSingleResponse>(`${endpoint}/classes`, data);
  };

  const updateClass = async (
    classId: string,
    data: Partial<IClassRequest>
  ): Promise<IClassSingleResponse> => {
    if (isMockData) {
      return Promise.resolve({ class: mockAcademicsClasses[0] as any });
    }
    return httpAPI.patch<IClassSingleResponse>(`${endpoint}/classes/${classId}`, data);
  };

  const deleteClass = async (classId: string): Promise<void> => {
    if (isMockData) {
      return Promise.resolve();
    }
    return httpAPI.delete(`${endpoint}/classes/${classId}`);
  };

  // ============================================================
  // Courses API
  // ============================================================

  const getCourses = async (
    params?: ICourseListQueryParams
  ): Promise<ICourseListResponse> => {
    if (isMockData) {
      return Promise.resolve({
        courses: mockAcademicsCourses as any,
        total: mockAcademicsCourses.length,
        pagination: {
          page: params?.page || 1,
          limit: params?.limit || 10,
          total: mockAcademicsCourses.length,
          totalPages: Math.ceil(mockAcademicsCourses.length / (params?.limit || 10)),
        },
      });
    }
    return httpAPI.get<ICourseListResponse>(`${endpoint}/courses`, { params });
  };

  const getCourseById = async (courseId: string): Promise<ICourseSingleResponse> => {
    if (isMockData) {
      const mockCourse = mockAcademicsCourses[0];
      return Promise.resolve({ course: mockCourse as any });
    }
    return httpAPI.get<ICourseSingleResponse>(`${endpoint}/courses/${courseId}`);
  };

  const createCourse = async (data: ICourseRequest): Promise<ICourseSingleResponse> => {
    if (isMockData) {
      return Promise.resolve({ course: mockAcademicsCourses[0] as any });
    }
    return httpAPI.post<ICourseSingleResponse>(`${endpoint}/courses`, data);
  };

  const updateCourse = async (
    courseId: string,
    data: Partial<ICourseRequest>
  ): Promise<ICourseSingleResponse> => {
    if (isMockData) {
      return Promise.resolve({ course: mockAcademicsCourses[0] as any });
    }
    return httpAPI.patch<ICourseSingleResponse>(`${endpoint}/courses/${courseId}`, data);
  };

  const deleteCourse = async (courseId: string): Promise<void> => {
    if (isMockData) {
      return Promise.resolve();
    }
    return httpAPI.delete(`${endpoint}/courses/${courseId}`);
  };

  // ============================================================
  // Schedules API
  // ============================================================

  const getSchedules = async (
    params?: IScheduleListQueryParams
  ): Promise<IScheduleListResponse> => {
    if (isMockData) {
      return Promise.resolve({
        schedules: mockAcademicsSchedules as any,
        total: mockAcademicsSchedules.length,
        pagination: {
          page: params?.page || 1,
          limit: params?.limit || 10,
          total: mockAcademicsSchedules.length,
          totalPages: Math.ceil(mockAcademicsSchedules.length / (params?.limit || 10)),
        },
      });
    }
    return httpAPI.get<IScheduleListResponse>(`${endpoint}/schedules`, { params });
  };

  const getScheduleById = async (
    scheduleId: string,
    includePeriods?: boolean
  ): Promise<IScheduleSingleResponse> => {
    if (isMockData) {
      return Promise.resolve({ schedule: mockAcademicsSchedules[0] as any });
    }
    const params = includePeriods ? { includePeriods: true } : undefined;
    return httpAPI.get<IScheduleSingleResponse>(`${endpoint}/schedules/${scheduleId}`, { params });
  };

  const createSchedule = async (data: IScheduleRequest): Promise<IScheduleSingleResponse> => {
    if (isMockData) {
      return Promise.resolve({ schedule: mockAcademicsSchedules[0] as any });
    }
    return httpAPI.post<IScheduleSingleResponse>(`${endpoint}/schedules`, data);
  };

  // ============================================================
  // Assessments API
  // ============================================================

  const getAssessments = async (
    params?: IAssessmentListQueryParams
  ): Promise<IAssessmentListResponse> => {
    if (isMockData) {
      // Return empty assessments for mock since we don't have a direct mock
      return Promise.resolve({
        assessments: [],
        total: 0,
        pagination: {
          page: params?.page || 1,
          limit: params?.limit || 10,
          total: 0,
          totalPages: 0,
        },
      });
    }
    return httpAPI.get<IAssessmentListResponse>(`${endpoint}/assessments`, { params });
  };

  const getAssessmentById = async (assessmentId: string): Promise<IAssessmentSingleResponse> => {
    if (isMockData) {
      return Promise.resolve({ assessment: academicAssessmentDetailsMock as any });
    }
    return httpAPI.get<IAssessmentSingleResponse>(`${endpoint}/assessments/${assessmentId}`);
  };

  const createAssessment = async (data: IAssessmentRequest): Promise<IAssessmentSingleResponse> => {
    if (isMockData) {
      return Promise.resolve({ assessment: academicAssessmentDetailsMock as any });
    }
    return httpAPI.post<IAssessmentSingleResponse>(`${endpoint}/assessments`, data);
  };

  const updateAssessment = async (
    assessmentId: string,
    data: IAssessmentRequest
  ): Promise<IAssessmentSingleResponse> => {
    if (isMockData) {
      return Promise.resolve({ assessment: academicAssessmentDetailsMock as any });
    }
    return httpAPI.patch<IAssessmentSingleResponse>(`${endpoint}/assessments/${assessmentId}`, data);
  };

  const updateSubjectScores = async (
    assessmentId: string,
    data: IUpdateSubjectScoresRequest
  ): Promise<IAcademicsAssessmentDetails> => {
    if (isMockData) {
      return Promise.resolve(academicAssessmentDetailsMock);
    }
    return httpAPI.patch<IAcademicsAssessmentDetails>(
      `${endpoint}/assessments/${assessmentId}/subjects/${data.subject.courseId}/scores`,
      data
    );
  };

  // Legacy assessment methods (for existing UI compatibility)
  const getAcademicsAssessmentsForClasses = async (
    params: TPaginationParams
  ): Promise<IAcademicsAssessmentClassMapResponse> => {
    if (isMockData) {
      return Promise.resolve({
        classAssessments: academicsAssessmentClassesMock,
        statistics: mockAcademicsAssessmentClassStatistics,
      });
    }
    const response = await httpAPI.get<IAcademicsAssessmentClassMapResponse>(
      `${endpoint}/assessments/classes`,
      { params }
    );
    return response;
  };

  const getAcademicsAssessmentsForCourses =
    async (): Promise<IAcademicsAssessmentCourseMapResponse> => {
      if (isMockData) {
        return Promise.resolve({
          courseAssessments: academicsAssessmentCoursesMock,
          statistics: academicsAssessmentCourseStatisticsMock,
        });
      }
      const response = await httpAPI.get<IAcademicsAssessmentCourseMapResponse>(
        `${endpoint}/assessments`,
        { params: { groupBy: 'course' } }
      );
      return response;
    };

  const getAcademicsAssessmentDetails = async (
    assessmentId: string
  ): Promise<IAcademicsAssessmentDetails> => {
    if (isMockData) {
      return Promise.resolve(academicAssessmentDetailsMock);
    }
    const response = await httpAPI.get<IAcademicsAssessmentDetails>(
      `${endpoint}/assessments/${assessmentId}`
    );
    return response;
  };

  // ============================================================
  // Lesson Plans API
  // ============================================================

  const getLessonPlans = async (
    params?: ILessonPlanListQueryParams
  ): Promise<ILessonPlanListResponse> => {
    if (isMockData) {
      return Promise.resolve({
        lessonPlans: mockAcademicsLessonPlanList as any,
        total: mockAcademicsLessonPlanList.length,
        pagination: {
          page: params?.page || 1,
          limit: params?.limit || 10,
          total: mockAcademicsLessonPlanList.length,
          totalPages: Math.ceil(mockAcademicsLessonPlanList.length / (params?.limit || 10)),
        },
      });
    }
    return httpAPI.get<ILessonPlanListResponse>(`${endpoint}/lesson-plans`, { params });
  };

  const getLessonPlanById = async (
    lessonPlanId: string,
    includeUnits?: boolean
  ): Promise<ILessonPlanSingleResponse> => {
    if (isMockData) {
      return Promise.resolve({ lessonPlan: mockAcademicsLessonPlanList[0] as any });
    }
    const params = includeUnits ? { includeUnits: true } : undefined;
    return httpAPI.get<ILessonPlanSingleResponse>(`${endpoint}/lesson-plans/${lessonPlanId}`, { params });
  };

  const createLessonPlan = async (data: ILessonPlanRequest): Promise<ILessonPlanSingleResponse> => {
    if (isMockData) {
      return Promise.resolve({ lessonPlan: mockAcademicsLessonPlanList[0] as any });
    }
    return httpAPI.post<ILessonPlanSingleResponse>(`${endpoint}/lesson-plans`, data);
  };

  const updateLessonPlan = async (lessonPlanId: string, data: Partial<ILessonPlanRequest['lessonPlan']>): Promise<ILessonPlanSingleResponse> => {
    if (isMockData) {
      return Promise.resolve({ lessonPlan: mockAcademicsLessonPlanList[0] as any });
    }
    return httpAPI.patch<ILessonPlanSingleResponse>(`${endpoint}/lesson-plans/${lessonPlanId}`, data);
  };

  const deleteLessonPlan = async (lessonPlanId: string): Promise<void> => {
    if (isMockData) return;
    return httpAPI.delete(`${endpoint}/lesson-plans/${lessonPlanId}`);
  };

  // ============================================================
  // Materials API (Material Groups + Items)
  // ============================================================

  const createAcademicsMaterialsGroup = async (
    data: any
  ): Promise<IAcademicsMaterials> => {
    if (isMockData) {
      return Promise.resolve(mockAcademicsMaterials[0]);
    }

    // Map frontend data to backend format
    const request = {
      material: {
        instituteId: data.instituteId,
        name: data.name,
        description: data.description,
        category: data.category,
        status: data.status,
        condition: data.condition,
        purchaseDate: data.purchaseDate?.toISOString(),
        lastMaintenanceDate: data.lastMaintenanceDate?.toISOString(),
        location: data.location,
        quantity: data.quantity,
        minimumQuantity: data.minimumQuantity,
        supplier: data.supplier,
        cost: data.cost,
        tags: data.tags,
        department: data.department,
        notes: data.notes,
        receiptDocumentIds: data.documents?.receipts?.map((d: any) => d.id) || [],
        invoiceDocumentIds: data.documents?.invoices?.map((d: any) => d.id) || [],
        paymentDocumentIds: data.documents?.payments?.map((d: any) => d.id) || [],
      },
    };

    const response = await httpAPI.post<any>(`${endpoint}/materials`, request);
    return mapGroupResponseToAcademicsMaterial(response.material);
  };

  const patchAcademicsMaterialsGroup = async (
    materialId: string,
    data: any
  ): Promise<IAcademicsMaterials> => {
    if (isMockData) {
      return Promise.resolve(mockAcademicsMaterials[0]);
    }

    const request = {
      material: {
        name: data.name,
        description: data.description,
        category: data.category,
        status: data.status,
        condition: data.condition,
        purchaseDate: data.purchaseDate?.toISOString(),
        lastMaintenanceDate: data.lastMaintenanceDate?.toISOString(),
        location: data.location,
        quantity: data.quantity,
        minimumQuantity: data.minimumQuantity,
        supplier: data.supplier,
        cost: data.cost,
        tags: data.tags,
        department: data.department,
        notes: data.notes,
        receiptDocumentIds: data.documents?.receipts?.map((d: any) => d.id) || [],
        invoiceDocumentIds: data.documents?.invoices?.map((d: any) => d.id) || [],
        paymentDocumentIds: data.documents?.payments?.map((d: any) => d.id) || [],
      },
    };

    const response = await httpAPI.patch<any>(`${endpoint}/materials/${materialId}`, request);
    return mapGroupResponseToAcademicsMaterial(response.material);
  };

  const addAcademicsMaterial = async (
    materialGroupId: string,
    data: any
  ): Promise<TAcademicMaterial> => {
    if (isMockData) {
      return Promise.resolve(mockAcademicsMaterials[0].materials[0]);
    }

    const request = {
      item: {
        itemType: data.type,
        name: data.name,
        description: data.description,
        quantity: data.quantity,
        status: data.status,
        // Book fields
        ...(data.type === 'book' && {
          isbn: data.isbn,
          author: data.author,
          publisher: data.publisher,
          edition: data.edition,
          publicationYear: data.publicationYear,
          subject: data.subject,
          gradeLevel: data.gradeLevel,
          language: data.language,
          isDigital: data.isDigital,
          digitalFormat: data.digitalFormat,
          digitalUrl: data.digitalUrl,
        }),
        // Lab Equipment fields
        ...(data.type === 'lab_equipment' && {
          equipmentType: data.equipmentType,
          modelNumber: data.modelNumber,
          manufacturer: data.manufacturer,
          serialNumber: data.serialNumber,
          calibrationDate: data.calibrationDate?.toISOString(),
          nextCalibrationDate: data.nextCalibrationDate?.toISOString(),
          powerRequirements: data.powerRequirements,
          safetyInstructions: data.safetyInstructions,
          maintenanceSchedule: data.maintenanceSchedule && {
            frequency: data.maintenanceSchedule.frequency?.toString(),
            lastMaintenance: data.maintenanceSchedule.lastMaintenance?.toISOString?.() || data.maintenanceSchedule.lastMaintenance,
            nextMaintenance: data.maintenanceSchedule.nextMaintenance?.toISOString?.() || data.maintenanceSchedule.nextMaintenance,
            maintenanceType: data.maintenanceSchedule.maintenanceType,
            maintenanceNotes: data.maintenanceSchedule.maintenanceNotes,
          },
          isPortable: data.isPortable,
          requiresTraining: data.requiresTraining,
        }),
        // Digital Resource fields
        ...(data.type === 'digital_resource' && {
          resourceType: data.resourceType,
          format: data.format,
          fileSize: data.fileSize?.toString(),
          url: data.url,
          accessLevel: data.accessLevel,
          licenseType: data.licenseType,
          expiryDate: data.expiryDate?.toISOString(),
          compatibleDevices: data.compatibleDevices,
          requiresInternet: data.requiresInternet,
        }),
        // Stationery fields
        ...(data.type === 'stationery' && {
          stationeryType: data.stationeryType,
          brand: data.brand,
          color: data.color,
          size: data.size,
          isConsumable: data.isConsumable,
          expiryDate: data.expiryDate?.toISOString(),
        }),
      },
    };

    const response = await httpAPI.post<any>(`${endpoint}/materials/${materialGroupId}/items`, request);
    return mapItemResponseToAcademicMaterial(response.item, data.category);
  };

  const patchAcademicsMaterial = async (
    materialGroupId: string,
    itemId: string,
    data: any
  ): Promise<TAcademicMaterial> => {
    if (isMockData) {
      return Promise.resolve(mockAcademicsMaterials[0].materials[0]);
    }

    const request = {
      item: {
        name: data.name,
        description: data.description,
        quantity: data.quantity,
        status: data.status,
        // Type-specific fields (same as addAcademicsMaterial)
        ...(data.type === 'book' && {
          isbn: data.isbn,
          author: data.author,
          publisher: data.publisher,
          edition: data.edition,
          publicationYear: data.publicationYear,
          subject: data.subject,
          gradeLevel: data.gradeLevel,
          language: data.language,
          isDigital: data.isDigital,
          digitalFormat: data.digitalFormat,
          digitalUrl: data.digitalUrl,
        }),
        ...(data.type === 'lab_equipment' && {
          equipmentType: data.equipmentType,
          modelNumber: data.modelNumber,
          manufacturer: data.manufacturer,
          serialNumber: data.serialNumber,
          calibrationDate: data.calibrationDate?.toISOString(),
          nextCalibrationDate: data.nextCalibrationDate?.toISOString(),
          powerRequirements: data.powerRequirements,
          safetyInstructions: data.safetyInstructions,
          maintenanceSchedule: data.maintenanceSchedule && {
            frequency: data.maintenanceSchedule.frequency?.toString(),
            lastMaintenance: data.maintenanceSchedule.lastMaintenance?.toISOString?.() || data.maintenanceSchedule.lastMaintenance,
            nextMaintenance: data.maintenanceSchedule.nextMaintenance?.toISOString?.() || data.maintenanceSchedule.nextMaintenance,
            maintenanceType: data.maintenanceSchedule.maintenanceType,
            maintenanceNotes: data.maintenanceSchedule.maintenanceNotes,
          },
          isPortable: data.isPortable,
          requiresTraining: data.requiresTraining,
        }),
        ...(data.type === 'digital_resource' && {
          resourceType: data.resourceType,
          format: data.format,
          fileSize: data.fileSize?.toString(),
          url: data.url,
          accessLevel: data.accessLevel,
          licenseType: data.licenseType,
          expiryDate: data.expiryDate?.toISOString(),
          compatibleDevices: data.compatibleDevices,
          requiresInternet: data.requiresInternet,
        }),
        ...(data.type === 'stationery' && {
          stationeryType: data.stationeryType,
          brand: data.brand,
          color: data.color,
          size: data.size,
          isConsumable: data.isConsumable,
          expiryDate: data.expiryDate?.toISOString(),
        }),
      },
    };

    const response = await httpAPI.patch<any>(`${endpoint}/materials/${materialGroupId}/items/${itemId}`, request);
    return mapItemResponseToAcademicMaterial(response.item, data.category);
  };

  const deleteAcademicsMaterialItem = async (materialGroupId: string, itemId: string): Promise<void> => {
    if (isMockData) return;
    return httpAPI.delete(`${endpoint}/materials/${materialGroupId}/items/${itemId}`);
  };

  // Helper functions to map backend response to frontend types
  const mapGroupResponseToAcademicsMaterial = (response: any): IAcademicsMaterials => {
    return {
      id: response.id,
      name: response.name,
      description: response.description || '',
      category: response.category as any,
      status: response.status as any,
      condition: response.condition as any,
      purchaseDate: new Date(response.purchaseDate || new Date()),
      lastMaintenanceDate: response.lastMaintenanceDate ? new Date(response.lastMaintenanceDate) : undefined,
      location: response.location || '',
      quantity: response.quantity || 0,
      minimumQuantity: response.minimumQuantity || 0,
      supplier: response.supplier,
      cost: response.cost || 0,
      tags: response.tags || [],
      department: response.department || '',
      notes: response.notes,
      documents: {
        receipts: [],
        invoices: [],
        payments: [],
      },
      materials: response.items?.map((item: any) => mapItemResponseToAcademicMaterial(item, response.category)) || [],
      totalCount: response.rollups?.totalCount || 0,
      availableCount: response.rollups?.availableCount || 0,
      inUseCount: response.rollups?.inUseCount || 0,
      maintenanceCount: response.rollups?.maintenanceCount || 0,
      retiredCount: response.rollups?.retiredCount || 0,
      totalValue: response.rollups?.totalValue || 0,
      lastInventoryDate: response.rollups?.lastInventoryDate ? new Date(response.rollups.lastInventoryDate) : new Date(),
      actions: {
        created: {
          userId: response.createdBy || '',
          timestamp: new Date(response.createdAt).getTime(),
        },
      },
    };
  };

  const mapItemResponseToAcademicMaterial = (item: any, category: string): TAcademicMaterial => {
    const baseFields = {
      id: item.id,
      name: item.name,
      description: item.description || '',
      category: category as any,
      status: item.status as any,
      condition: 'good' as any,
      purchaseDate: new Date(),
      location: '',
      quantity: item.quantity || 1,
      minimumQuantity: 0,
      supplier: '',
      cost: 0,
      tags: [],
      actions: {
        created: {
          userId: item.createdBy || '',
          timestamp: new Date(item.createdAt).getTime(),
        },
      },
    };

    switch (item.itemType) {
      case 'book':
        return {
          ...baseFields,
          type: 'book',
          isbn: item.isbn || '',
          author: item.author || '',
          publisher: item.publisher || '',
          edition: item.edition || '',
          publicationYear: item.publicationYear || new Date().getFullYear(),
          subject: item.subject || '',
          gradeLevel: item.gradeLevel || '',
          language: item.language || '',
          isDigital: item.isDigital || false,
          digitalFormat: item.digitalFormat as any,
          digitalUrl: item.digitalUrl,
        } as IBook;

      case 'lab_equipment':
        return {
          ...baseFields,
          type: 'lab_equipment',
          equipmentType: item.equipmentType as any,
          modelNumber: item.modelNumber || '',
          manufacturer: item.manufacturer || '',
          serialNumber: item.serialNumber || '',
          calibrationDate: item.calibrationDate ? new Date(item.calibrationDate) : undefined,
          nextCalibrationDate: item.nextCalibrationDate ? new Date(item.nextCalibrationDate) : undefined,
          powerRequirements: item.powerRequirements,
          safetyInstructions: item.safetyInstructions || '',
          maintenanceSchedule: item.maintenanceSchedule || {
            frequency: 0,
            lastMaintenance: new Date(),
            nextMaintenance: new Date(),
            maintenanceType: '',
          },
          isPortable: item.isPortable || false,
          requiresTraining: item.requiresTraining || false,
        } as ILabEquipment;

      case 'digital_resource':
        return {
          ...baseFields,
          type: 'digital_resource',
          resourceType: item.resourceType as any,
          format: item.format || '',
          fileSize: parseInt(item.fileSize || '0'),
          url: item.url || '',
          accessLevel: item.accessLevel as any,
          licenseType: item.licenseType as any,
          expiryDate: item.expiryDate ? new Date(item.expiryDate) : undefined,
          compatibleDevices: item.compatibleDevices || [],
          requiresInternet: item.requiresInternet || false,
        } as IDigitalResource;

      case 'stationery':
        return {
          ...baseFields,
          type: 'stationery',
          stationeryType: item.stationeryType as any,
          brand: item.brand || '',
          color: item.color,
          size: item.size,
          isConsumable: item.isConsumable || false,
          expiryDate: item.expiryDate ? new Date(item.expiryDate) : undefined,
        } as IStationery;

      default:
        throw new Error(`Unknown item type: ${item.itemType}`);
    }
  };

  // ============================================================
  // Legacy Methods (for existing UI compatibility)
  // ============================================================

  // Legacy: Get courses - calls real non-admin courses API and maps to legacy shape
  const getAcademicsCourses = async (
    params?: { page?: number; limit?: number; academicYear?: string; search?: string }
  ): Promise<IAcademicsCourseResponse> => {
    if (isMockData) {
      return Promise.resolve({
        courses: mockAcademicsCourses,
        total: mockAcademicsCourses.length,
      });
    }
    const response = await httpAPI.get<any>(`${endpoint}/courses`, { params });
    const data: any[] = response.data ?? response.courses ?? [];
    const total: number = response.total ?? response.count ?? data.length;
    const courses = data.map((c: any) => ({
      id: c.id,
      name: c.name,
      description: c.description || '',
      subjectCode: c.code || c.subjectCode || '',
      classIds: c.classIds || [],
      teacherId: c.teacherId || 0,
      subjectType: c.subjectType || 'core',
      isElective: c.isElective || false,
      createdAt: c.createdAt,
      updatedAt: c.updatedAt,
    }));
    return { courses, total };
  };

  // Legacy: Get classes - calls real non-admin classes API and maps to legacy shape
  const getAcademicsClasses = async (
    params?: { page?: number; limit?: number; academicYear?: string; grade?: string; search?: string }
  ): Promise<IAcademicsClassResponse> => {
    if (isMockData) {
      return Promise.resolve({
        classes: mockAcademicsClasses,
        total: mockAcademicsClasses.length,
      });
    }
    const response = await httpAPI.get<any>(`${endpoint}/classes`, { params });
    const data: any[] = response.data ?? response.classes ?? [];
    const total: number = response.total ?? response.count ?? data.length;
    const classes = data.map((c: any) => ({
      id: c.id,
      name: c.name,
      description: c.description || '',
      section: c.section,
      sections: c.sections || [],
      standardLevel: c.standardLevel?.name || c.standardLevel || c.grade || '',
      board: c.board,
      academicYear: c.academicYear || '',
      classTutor: c.classTutor?.id || c.classTutorId,
      strength: c.strength || 0,
      stream: c.stream,
      courseIds: c.courseIds || [],
      createdAt: c.createdAt,
      updatedAt: c.updatedAt,
    }));
    return { classes, total };
  };

  // Legacy: Get schedules - calls real schedules API and maps to legacy shape
  const getAcademicsSchedules = async (
    params?: { page?: number; limit?: number; academicYear?: string; grade?: string; classId?: string }
  ): Promise<IAcademicsScheduleResponse> => {
    if (isMockData) {
      return Promise.resolve({
        schedules: mockAcademicsSchedules,
        total: mockAcademicsSchedules.length,
      });
    }
    const response = await httpAPI.get<any>(`${endpoint}/schedules`, { params });
    const data: any[] = response.data ?? response.schedules ?? [];
    const total: number = response.total ?? response.count ?? data.length;
    const schedules = data.map((s: any) => ({
      id: s.id,
      name: s.name,
      description: s.description || '',
      type: s.type || 'weekly',
      academicYear: s.academicYear || '',
      term: s.term,
      isActive: s.isActive ?? true,
      createdAt: s.createdAt,
      updatedAt: s.updatedAt,
      periods: (s.periods || []).map((p: any) => ({
        id: p.id,
        periodNumber: p.periodNumber,
        startTime: p.startTime,
        endTime: p.endTime,
        dayOfWeek: p.dayOfWeek,
        courseId: p.course?.id || p.courseId,
        courseName: p.course?.name || p.courseName || '',
        teacherId: p.teacher?.id || p.teacherId,
        teacherName: p.teacher?.name || p.teacherName || '',
        classId: p.classId || s.class?.id,
        className: p.className || s.class?.name || '',
        room: p.room || '',
        isBreak: p.isBreak || false,
      })),
      instituteId: s.institute?.id || s.instituteId,
      classId: s.class?.id || s.classId,
    }));
    return { schedules, total };
  };

  // Legacy: Create schedule (with API)
  const createAcademicsSchedule = async (
    schedule: IAcademicsSchedule
  ): Promise<IAcademicsSchedule> => {
    if (isMockData) {
      return Promise.resolve(mockAcademicsSchedules[0]);
    }
    const requestObj = { schedule };
    const response = await httpAPI.post<IAcademicsSchedule>(
      `${endpoint}/schedules`,
      requestObj
    );
    return response;
  };

  // Legacy: Patch schedule
  const patchAcademicsSchedule = async (
    schedule: IAcademicsSchedule
  ): Promise<IAcademicsSchedule> => {
    if (isMockData) {
      return Promise.resolve(mockAcademicsSchedules[0]);
    }
    // Backend does not yet have a PATCH /schedules/:id endpoint
    return Promise.resolve(mockAcademicsSchedules[0]);
  };

  const deleteAcademicsSchedule = async (scheduleId: string): Promise<void> => {
    if (isMockData) return;
    return httpAPI.delete(`${endpoint}/schedules/${scheduleId}`);
  };

  const deleteAcademicsMaterialGroup = async (materialGroupId: string): Promise<void> => {
    if (isMockData) return;
    return httpAPI.delete(`${endpoint}/materials/${materialGroupId}`);
  };

  // Materials (legacy mock - kept for backwards compatibility)
  // TODO: Naman - Add assignedTo student/class support to materials API
  const getAcademicsMaterials = async (
    params?: {
      page?: number;
      limit?: number;
      academicYear?: string;
      grade?: string;
      classId?: string;
      assignedToStudentIds?: string[];
      assignedToClassIds?: string[];
    }
  ): Promise<IAcademicsMaterialsResponse> => {
    if (isMockData) {
      let materials = [...mockAcademicsMaterials];
      if (params?.assignedToStudentIds?.length) {
        materials = materials.filter(
          (m) =>
            m.assignedTo?.studentIds?.some((id) =>
              params.assignedToStudentIds!.includes(id)
            )
        );
      }
      if (params?.assignedToClassIds?.length) {
        materials = materials.filter(
          (m) =>
            m.assignedTo?.classIds?.some((id) =>
              params.assignedToClassIds!.includes(id)
            )
        );
      }
      if (params?.grade) {
        materials = materials.filter((m) =>
          m.materials.some(
            (item) => 'gradeLevel' in item && item.gradeLevel?.includes(params.grade!)
          )
        );
      }
      return Promise.resolve({
        materials,
        total: materials.length,
      });
    }

    const response = await httpAPI.get<any>(`${endpoint}/materials`, { params });

    // Map backend response to frontend format
    const materials = response.materials.map((material: any) =>
      mapGroupResponseToAcademicsMaterial(material)
    );

    return {
      materials,
      total: response.total,
    };
  };

  // Library (mock only - not in backend docs for academics)
  const getAcademicsLibrary = async (): Promise<IAcademicsLibraryResponse> => {
    if (isMockData) {
      return Promise.resolve({
        items: mockAcademicsLibrary,
        total: mockAcademicsLibrary.length,
      });
    }
    return httpAPI.inventory.get<IAcademicsLibraryResponse>('/library');
  };

  const createAcademicsLibraryItem = async (
    item: IAcademicsLibraryItem
  ): Promise<IAcademicsLibraryItem> => {
    if (isMockData) {
      return Promise.resolve(mockAcademicsLibrary[0]);
    }
    return httpAPI.inventory.post<IAcademicsLibraryItem>('/library', item);
  };

  const patchAcademicsLibraryItem = async (
    item: IAcademicsLibraryItem
  ): Promise<IAcademicsLibraryItem> => {
    if (isMockData) {
      return Promise.resolve(mockAcademicsLibrary[0]);
    }
    return httpAPI.inventory.patch<IAcademicsLibraryItem>(`/library/${item.id}`, item);
  };

  const createAcademicsLibraryBorrowingRecord = async (
    request: ILibraryBorrowingRequest
  ): Promise<IBorrowingRecord> => {
    if (isMockData) {
      return Promise.resolve(mockAcademicsLibrary[0].borrowingHistory[0]);
    }
    const response = await httpAPI.inventory.post<ILibraryBorrowingResponse>(
      '/library/borrowings',
      request
    );
    return {
      recordId: response.id,
      assetId: response.inventory?.id || request.borrowingRecord.inventoryId,
      borrowerId: response.borrower?.id || request.borrowingRecord.borrowerId,
      borrowerType: BorrowerType.STUDENT,
      borrowerName: response.borrower?.name || '',
      borrowedDate: response.borrowedDate,
      dueDate: response.dueDate,
      returnDate: response.returnedDate,
      status: response.status as any,
      renewalCount: response.renewalCount || 0,
      fineAmount: response.fineAmount,
      notes: response.notes,
      issuedBy: '',
      returnedTo: undefined,
      createdAt: response.createdAt,
      updatedAt: response.createdAt,
    } as IBorrowingRecord;
  };

  const patchAcademicsLibraryBorrowingRecord = async (
    recordId: string,
    request: ILibraryBorrowingRequest
  ): Promise<IBorrowingRecord> => {
    if (isMockData) {
      return Promise.resolve(mockAcademicsLibrary[0].borrowingHistory[0]);
    }
    const response = await httpAPI.inventory.patch<ILibraryBorrowingResponse>(
      `/library/borrowings/${recordId}`,
      request
    );
    return {
      recordId: response.id,
      assetId: response.inventory?.id || request.borrowingRecord.inventoryId,
      borrowerId: response.borrower?.id || request.borrowingRecord.borrowerId,
      borrowerType: BorrowerType.STUDENT,
      borrowerName: response.borrower?.name || '',
      borrowedDate: response.borrowedDate,
      dueDate: response.dueDate,
      returnDate: response.returnedDate,
      status: response.status as any,
      renewalCount: response.renewalCount || 0,
      fineAmount: response.fineAmount,
      notes: response.notes,
      issuedBy: '',
      returnedTo: undefined,
      createdAt: response.createdAt,
      updatedAt: response.createdAt,
    } as IBorrowingRecord;
  };

  // Legacy lesson plans
  const getAcademicsLessonPlans = async (
    params?: { page?: number; limit?: number; academicYear?: string; grade?: string }
  ): Promise<ILessonPlanResponse> => {
    if (isMockData) {
      return Promise.resolve({
        lessonPlans: mockAcademicsLessonPlanList,
        total: mockAcademicsLessonPlanList.length,
      });
    }
    const response = await httpAPI.get<ILessonPlanResponse>(
      `${endpoint}/lesson-plans`,
      { params }
    );
    return response;
  };

  return {
    // New API methods - Classes
    getClasses,
    getClassById,
    createClass,
    updateClass,
    deleteClass,

    // New API methods - Courses
    getCourses,
    getCourseById,
    createCourse,
    updateCourse,
    deleteCourse,

    // New API methods - Schedules
    getSchedules,
    getScheduleById,
    createSchedule,

    // New API methods - Assessments
    getAssessments,
    getAssessmentById,
    createAssessment,
    updateAssessment,
    updateSubjectScores,

    // New API methods - Lesson Plans
    getLessonPlans,
    getLessonPlanById,
    createLessonPlan,
    updateLessonPlan,
    deleteLessonPlan,

    // New API methods - Materials
    createAcademicsMaterialsGroup,
    patchAcademicsMaterialsGroup,
    addAcademicsMaterial,
    patchAcademicsMaterial,
    deleteAcademicsMaterialItem,

    // Legacy methods (for existing UI compatibility)
    getAcademicsCourses,
    getAcademicsClasses,
    getAcademicsSchedules,
    createAcademicsSchedule,
    patchAcademicsSchedule,
    deleteAcademicsSchedule,
    deleteAcademicsMaterialGroup,
    getAcademicsAssessmentsForClasses,
    getAcademicsAssessmentsForCourses,
    getAcademicsAssessmentDetails,
    getAcademicsMaterials,
    getAcademicsLibrary,
    createAcademicsLibraryItem,
    patchAcademicsLibraryItem,
    createAcademicsLibraryBorrowingRecord,
    patchAcademicsLibraryBorrowingRecord,
    getAcademicsLessonPlans,
  };
};
